import { useState, useRef, useEffect } from 'react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import { CalIc, calInitials } from './calShared'

/**
 * Desktop calendar toolbar per the Claude Design "Calendar · five views":
 * ‹ title (with a date-jump mini-month dropdown) › · Today · groomer filter
 * chips (multi-select, "All" clears) · segmented view control · Add button.
 */

const TEXT = '#2E2A40'
const MUTED = '#8B85A4'
const BORDER = '#E7E2F3'
const PRIMARY = '#7C6BD9'
const PRIMARY_DEEP = '#5F4EC2'
const PRIMARY_SOFT = '#EDE9FB'
const FONT = "'Outfit', sans-serif"

const VIEWS = [
	{ id: 1, label: 'Day' },
	{ id: 3, label: '3 day' },
	{ id: 5, label: '5 day' },
	{ id: 7, label: 'Week' },
	{ id: 'month', label: 'Month' },
]

const rangeFor = (view, anchor) => {
	if (view === 'month') return []
	return Array.from({ length: view }, (_, i) => anchor.add(i, 'day'))
}

const titleFor = (view, anchor) => {
	if (view === 'month') return { main: anchor.format('MMMM'), sub: anchor.format('YYYY') }
	if (view === 1) return { main: anchor.format('ddd, MMM D'), sub: anchor.format('YYYY') }
	const end = anchor.add(view - 1, 'day')
	const main = anchor.isSame(end, 'month')
		? `${anchor.format('MMM D')} – ${end.format('D')}`
		: `${anchor.format('MMM D')} – ${end.format('MMM D')}`
	return { main, sub: anchor.format('YYYY') }
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// Years shown per page in the year picker (3 columns × 4 rows).
const YEAR_PAGE = 12

// Caret beside the month / year labels — the affordance that says the label is
// itself a dropdown. Matches DropdownCalendar's header.
function HeadCaret({ open }) {
	return (
		<Box component="span" sx={{
			display: 'flex', flex: 'none', color: open ? PRIMARY_DEEP : MUTED,
			transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none',
		}}>
			<CalIc name="chevD" size={11} sw={2} />
		</Box>
	)
}

// ── date-jump mini month ────────────────────────────────────────────
function CalDateJump({ anchor, view, onPick }) {
	const [viewMonth, setViewMonth] = useState(anchor.startOf('month'))
	// Header drill-down: the month and year labels each open their own picker.
	const [mode, setMode] = useState('day')
	const [yearStart, setYearStart] = useState(() => anchor.year() - 6)
	const today = dayjs()
	const inRange = rangeFor(view, anchor).map(d => d.format('YYYY-MM-DD'))

	const first = viewMonth.startOf('month')
	const gridStart = first.subtract(first.day(), 'day')
	const weeks = Math.ceil((first.day() + viewMonth.daysInMonth()) / 7)
	const cells = Array.from({ length: weeks * 7 }, (_, i) => gridStart.add(i, 'day'))

	const navBtnSx = {
		width: 26, height: 26, border: `1.5px solid ${BORDER}`, borderRadius: '8px',
		bgcolor: '#FFFFFF', color: MUTED, display: 'flex', alignItems: 'center',
		justifyContent: 'center', cursor: 'pointer', p: 0,
		'&:hover': { color: PRIMARY_DEEP, borderColor: PRIMARY },
	}

	// Clickable month / year labels in the header. The caret (see HeadCaret) is
	// what marks them as drill-downs rather than plain text.
	const headBtnSx = (active) => ({
		display: 'inline-flex', alignItems: 'center', gap: '3px',
		border: 'none', borderRadius: '7px', px: '6px', py: '3px', fontFamily: FONT,
		bgcolor: active ? PRIMARY_SOFT : 'transparent',
		color: active ? PRIMARY_DEEP : TEXT,
		fontSize: '13.5px', fontWeight: 800, letterSpacing: '-0.01em',
		cursor: 'pointer', whiteSpace: 'nowrap',
		'&:hover': { bgcolor: active ? PRIMARY_SOFT : '#F3F1FA' },
	})

	// Cells in the month / year pickers.
	const gridBtnSx = (selected) => ({
		height: 34, border: 'none', borderRadius: '9px', fontFamily: FONT,
		bgcolor: selected ? PRIMARY : 'transparent',
		color: selected ? '#FFFFFF' : TEXT,
		fontSize: '12.5px', fontWeight: selected ? 800 : 650, cursor: 'pointer', p: 0,
		fontVariantNumeric: 'tabular-nums',
		'&:hover': { bgcolor: selected ? PRIMARY_DEEP : '#F3F1FA' },
	})

	const openMonthMode = () => setMode(m => (m === 'month' ? 'day' : 'month'))
	const openYearMode = () => {
		if (mode === 'year') { setMode('day'); return }
		setYearStart(viewMonth.year() - 6)
		setMode('year')
	}

	// Prev/next step by month, year or year-page depending on the active picker.
	const stepBy = (dir) => {
		if (mode === 'day') setViewMonth(m => m.add(dir, 'month'))
		else if (mode === 'month') setViewMonth(m => m.add(dir, 'year'))
		else setYearStart(s => s + dir * YEAR_PAGE)
	}
	const stepLabel = mode === 'day' ? 'month' : mode === 'month' ? 'year' : 'years'

	return (
		<Box role="dialog" aria-label="Jump to date" sx={{
			position: 'absolute', top: 'calc(100% + 7px)', left: 0, zIndex: 90, width: 276,
			bgcolor: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: '14px',
			boxShadow: '0 18px 44px rgba(46, 42, 64, 0.2)', p: '12px', fontFamily: FONT,
		}}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '2px 2px 9px 6px' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
					<Box component="button" type="button" aria-label="Select month"
						aria-expanded={mode === 'month'} sx={headBtnSx(mode === 'month')}
						onClick={openMonthMode}>
						<span>{viewMonth.format('MMMM')}</span>
						<HeadCaret open={mode === 'month'} />
					</Box>
					<Box component="button" type="button" aria-label="Select year"
						aria-expanded={mode === 'year'} sx={headBtnSx(mode === 'year')}
						onClick={openYearMode}>
						<span>{viewMonth.format('YYYY')}</span>
						<HeadCaret open={mode === 'year'} />
					</Box>
				</Box>
				<Box sx={{ display: 'flex', gap: '4px' }}>
					<Box component="button" type="button" aria-label={`Previous ${stepLabel}`} sx={navBtnSx}
						onClick={() => stepBy(-1)}>
						<CalIc name="chevL" size={13} sw={1.9} />
					</Box>
					<Box component="button" type="button" aria-label={`Next ${stepLabel}`} sx={navBtnSx}
						onClick={() => stepBy(1)}>
						<CalIc name="chevR" size={13} sw={1.9} />
					</Box>
				</Box>
			</Box>

			{mode === 'month' && (
				<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
					{MONTHS_SHORT.map((label, m) => (
						<Box component="button" type="button" key={label}
							aria-label={`${dayjs().month(m).format('MMMM')} ${viewMonth.format('YYYY')}`}
							sx={gridBtnSx(m === viewMonth.month())}
							onClick={() => { setViewMonth(v => v.month(m)); setMode('day') }}>
							{label}
						</Box>
					))}
				</Box>
			)}

			{mode === 'year' && (
				<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
					{Array.from({ length: YEAR_PAGE }, (_, i) => yearStart + i).map(y => (
						<Box component="button" type="button" key={y} aria-label={String(y)}
							sx={gridBtnSx(y === viewMonth.year())}
							onClick={() => { setViewMonth(v => v.year(y)); setMode('day') }}>
							{y}
						</Box>
					))}
				</Box>
			)}

			{mode === 'day' && (
			<>
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', pb: '4px' }}>
				{['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(w => (
					<Box key={w} component="span" sx={{
						textAlign: 'center', fontSize: '9.5px', fontWeight: 750,
						letterSpacing: '0.05em', textTransform: 'uppercase', color: '#A49DBD',
					}}>
						{w}
					</Box>
				))}
			</Box>
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
				{cells.map(d => {
					const ds = d.format('YYYY-MM-DD')
					const isSel = d.isSame(anchor, 'day')
					const isToday = d.isSame(today, 'day')
					return (
						<Box
							key={ds}
							component="button"
							type="button"
							onClick={() => onPick(d)}
							sx={{
								height: 30, border: 'none', bgcolor: 'transparent', borderRadius: '8px',
								fontSize: '12px', fontWeight: 650, color: TEXT, cursor: 'pointer', p: 0,
								fontFamily: FONT,
								'&:hover': { bgcolor: '#F3F1FA' },
								...(d.month() !== viewMonth.month() ? { color: '#C7C0DA' } : {}),
								...(inRange.includes(ds) ? { bgcolor: '#F5F3FC' } : {}),
								...(isSel ? {
									bgcolor: PRIMARY_SOFT, color: PRIMARY_DEEP, fontWeight: 800,
									boxShadow: `inset 0 0 0 1.5px ${PRIMARY}`,
								} : {}),
								...(isToday ? {
									bgcolor: PRIMARY, color: '#FFFFFF', fontWeight: 800,
									boxShadow: '0 2px 6px rgba(124, 107, 217, 0.4)',
									'&:hover': { bgcolor: PRIMARY_DEEP },
								} : {}),
							}}
						>
							{d.date()}
						</Box>
					)
				})}
			</Box>
			</>
			)}
		</Box>
	)
}

// ── toolbar ─────────────────────────────────────────────────────────
export default function CalendarToolbar({
	view,
	onViewChange,
	anchor,
	onAnchorChange,
	employees = [],
	selectedEmployees = [],
	onToggleEmployee,
	onClearEmployeeFilter,
	onAddAppointment,
}) {
	const [ddOpen, setDdOpen] = useState(false)
	const wrapRef = useRef(null)

	useEffect(() => {
		if (!ddOpen) return
		const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setDdOpen(false) }
		const onKey = (e) => { if (e.key === 'Escape') setDdOpen(false) }
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onKey)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onKey)
		}
	}, [ddOpen])

	const t = titleFor(view, anchor)
	const today = dayjs()
	const onToday = view === 'month'
		? anchor.isSame(today, 'month')
		: rangeFor(view, anchor).some(d => d.isSame(today, 'day'))

	const move = (dir) => {
		if (view === 'month') onAnchorChange(anchor.add(dir, 'month').startOf('month'))
		else onAnchorChange(anchor.add(dir * view, 'day'))
	}

	const allOn = selectedEmployees.length === 0
	const navBtnSx = {
		width: 34, height: 34, border: 'none', borderRadius: '10px', bgcolor: 'transparent',
		color: MUTED, display: 'flex', alignItems: 'center', justifyContent: 'center',
		cursor: 'pointer', p: 0,
		'&:hover': { bgcolor: '#F3F1FA', color: PRIMARY_DEEP },
	}

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '13px 20px 11px', flex: 'none', fontFamily: FONT }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
				<Box component="button" type="button" aria-label="Previous" sx={navBtnSx} onClick={() => move(-1)}>
					<CalIc name="chevL" size={16} sw={1.8} />
				</Box>
				<Box ref={wrapRef} sx={{ position: 'relative', minWidth: 0 }}>
					<Box
						component="button"
						type="button"
						aria-haspopup="dialog"
						aria-expanded={ddOpen}
						title="Jump to date"
						onClick={() => setDdOpen(!ddOpen)}
						sx={{
							display: 'flex', alignItems: 'baseline', gap: '7px', minWidth: 0,
							whiteSpace: 'nowrap', border: 'none', bgcolor: ddOpen ? PRIMARY_SOFT : 'transparent',
							p: '6px 10px', borderRadius: '10px', cursor: 'pointer', fontFamily: FONT,
							'&:hover': { bgcolor: ddOpen ? PRIMARY_SOFT : '#F3F1FA' },
							'& svg': {
								alignSelf: 'center', color: ddOpen ? PRIMARY_DEEP : MUTED,
								transition: 'transform 0.15s ease',
								transform: ddOpen ? 'rotate(180deg)' : 'none',
							},
						}}
					>
						<Box component="strong" sx={{ fontSize: '19px', fontWeight: 800, letterSpacing: '-0.02em', color: TEXT }}>
							{t.main}
						</Box>
						<Box component="span" sx={{ fontSize: '13px', fontWeight: 650, color: MUTED }}>
							{t.sub}
						</Box>
						<CalIc name="chevD" size={12} sw={2} />
					</Box>
					{ddOpen && (
						<CalDateJump
							anchor={anchor}
							view={view}
							onPick={(d) => { onAnchorChange(d); setDdOpen(false) }}
						/>
					)}
				</Box>
				<Box component="button" type="button" aria-label="Next" sx={navBtnSx} onClick={() => move(1)}>
					<CalIc name="chevR" size={16} sw={1.8} />
				</Box>
			</Box>

			<Box
				component="button"
				type="button"
				disabled={onToday}
				title={onToday ? 'You are viewing today' : 'Jump back to today'}
				onClick={() => onAnchorChange(dayjs())}
				sx={{
					height: 34, px: '13px', border: `1.5px solid ${BORDER}`, borderRadius: '10px',
					bgcolor: '#FFFFFF', fontSize: '12.5px', fontWeight: 650, color: TEXT,
					cursor: 'pointer', ml: '2px', fontFamily: FONT,
					'&:hover': { borderColor: PRIMARY, color: PRIMARY_DEEP },
					'&:disabled': { opacity: 0.4, pointerEvents: 'none' },
				}}
			>
				Today
			</Box>

			<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
				{/* groomer filter chips (multi-select; All = no filter) */}
				<Box role="group" aria-label="Filter by employee" sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<Box
						component="button"
						type="button"
						onClick={onClearEmployeeFilter}
						sx={{
							height: 30, px: '11px', border: `1.5px solid ${allOn ? 'transparent' : BORDER}`,
							borderRadius: '99px', bgcolor: allOn ? PRIMARY_SOFT : '#FFFFFF',
							fontSize: '12px', fontWeight: 700, color: allOn ? PRIMARY_DEEP : MUTED,
							cursor: 'pointer', fontFamily: FONT,
							'&:hover': { color: PRIMARY_DEEP, borderColor: allOn ? 'transparent' : PRIMARY },
						}}
					>
						All
					</Box>
					{employees.map(emp => {
						const on = selectedEmployees.some(e => e.id === emp.id)
						const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
						return (
							<Box
								key={emp.id}
								component="button"
								type="button"
								title={name}
								onClick={() => onToggleEmployee(emp)}
								sx={{
									width: 30, height: 30, border: 'none', borderRadius: '50%',
									fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.02em',
									cursor: 'pointer', fontFamily: FONT, p: 0,
									bgcolor: on ? PRIMARY_SOFT : '#F0EEF6',
									color: on ? PRIMARY_DEEP : '#6E6688',
									boxShadow: on ? `0 0 0 2px #FBFAFE, 0 0 0 4px ${PRIMARY}` : 'none',
									'&:hover': on ? {} : { bgcolor: '#E7E2F3', color: TEXT },
								}}
							>
								{calInitials(name)}
							</Box>
						)
					})}
				</Box>

				{/* segmented view control */}
				<Box role="tablist" aria-label="Calendar view" sx={{
					display: 'flex', gap: '2px', bgcolor: '#FFFFFF',
					border: `1.5px solid ${BORDER}`, borderRadius: '11px', p: '3px',
				}}>
					{VIEWS.map(v => (
						<Box
							key={v.id}
							component="button"
							type="button"
							onClick={() => onViewChange(v.id)}
							sx={{
								border: 'none', borderRadius: '8px', p: '6px 12px', fontSize: '12.5px',
								cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: FONT,
								bgcolor: view === v.id ? PRIMARY_SOFT : 'transparent',
								color: view === v.id ? PRIMARY_DEEP : MUTED,
								fontWeight: view === v.id ? 750 : 650,
								'&:hover': { color: view === v.id ? PRIMARY_DEEP : TEXT },
							}}
						>
							{v.label}
						</Box>
					))}
				</Box>

				{/* add appointment */}
				<Box
					component="button"
					type="button"
					onClick={onAddAppointment}
					sx={{
						display: 'inline-flex', alignItems: 'center', gap: '7px', height: 36, px: '14px',
						border: 'none', borderRadius: '10px', bgcolor: PRIMARY, color: '#FFFFFF',
						fontSize: '13px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
						boxShadow: '0 2px 8px rgba(124, 107, 217, 0.28)', fontFamily: FONT,
						'&:hover': { bgcolor: PRIMARY_DEEP },
					}}
				>
					<CalIc name="plus" size={15} sw={2} />
					<span>Add appointment</span>
				</Box>
			</Box>
		</Box>
	)
}
